/* React Components */
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { supabase } from '../../services/Supabase';
import { streamOpenAIResponse } from '../../services/openaiResponse';
/* Providers */
import { useAuth } from '../../context/AuthContext';
import commonFunction from '../../utils/CommonFunction';
/* Components */
import SideBar from "../../components/SideBar";
import ChatBox from '../../components/ChatBox';
/* Styles and Images */
import CompanyLogoWhite from "../../assets/img/m-comp-logo-white.png";
import CompanyLogoDark from "../../assets/img/m-comp-logo-black.png";
import spinner from "../../assets/img/loader-new.svg";
import UpgradeRequired from '../../components/UpgradeRequired';
/* React Icons */
import { BsChatDots } from "react-icons/bs";
import { RiUser3Line } from "react-icons/ri";


const Dashboard = () => {
    /* Providers and Headers */
    const { convertN2lbr, convertTextPattern, getDataArray, formatRelativeDate, mainSystemInstructions } = commonFunction();
    const messagesEndRef = useRef(null);

    /* User Information */
    const { user, userTheme, selectedQuestions, userMetaData, hasARIAccess } = useAuth();
    const userId = user?.id ?? '';
    const user_nickname = userMetaData?.nickname ? userMetaData?.nickname : (user?.first_name ?? 'You');

    /* Use States / Setters */
    const companyLogo  = (userTheme === 'dark-mode') ? CompanyLogoWhite : CompanyLogoDark;
    const [inputMessage, setInputMessage] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    const [loadingMsg, setLoadingMsg] = useState(true);
    const [threadID, setThreadID] = useState('');
    const [systemInstruction, setSystemInstruction] = useState('');
    const [currentStreamingMessage, setCurrentStreamingMessage] = useState('');
    const [chatMessages, setChatMessages] = useState([]);

    // For Input Focus
    const [isBotTyping, setIsBotTyping] = useState(false);
    const [inputHeight, setInputHeight] = useState('auto');
    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            if (event.shiftKey) {
                return;
            }
            event.preventDefault();
            onSubmit(event);
        }
    };
    function onChange(e){
        setInputHeight(`${e.target.scrollHeight}px`);
        setInputMessage(e.target.value);
        if(e.target.value===''){
            setInputHeight('auto');
        }
    }

    const chatContainerRef = useRef(null);
    const formRef = useRef(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        textareaRef.current?.focus();
    }, [isBotTyping]);
    
    useEffect(() => {
        let isActive = true;

        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }

        setLoadingMsg(true);
        const getDailyRoutineMsg = async () => {
            try {
                const { data, error } = await supabase
                    .from('user_chat_history')
                    .select('*')
                    .eq('user_id', userId)
                    .is('category_id', null)
                    .order('id', { ascending: false })
                    .limit(1)
                    .maybeSingle();

                if (!isActive) return;

                if (error) {
                    console.error('Error fetching latest chat history:', error);
                    setLoadingMsg(false);
                    return null;
                }

                setThreadID(data?.thread_id ?? '');
                setLoadingMsg(false);

                // ✅ Always load messages normally
                const messagesArray = getDataArray(data?.messages);
                if (messagesArray.length > 0) {
                    setChatMessages(messagesArray);
                    setLoadingMsg(false);
                }
                return;
            } catch (err) {
                if (isActive) {
                    console.error(err);
                    setLoadingMsg(false);
                }
            }
        };

        const getSystemInstruction = async () => {
            let instructionText = await mainSystemInstructions();
            setSystemInstruction(instructionText);
        }

        getDailyRoutineMsg();
        getSystemInstruction();

        return () => {
            isActive = false; // cleanup for strict mode
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // -------------------- SCROLL --------------------
    useEffect(() => {
        scrollToBottom();
    }, [chatMessages, currentStreamingMessage]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // -------------------- SAVE MESSAGES --------------------
    const onSubmit = async (e) => {
        e.preventDefault();
    
        // ✅ allow programmatic submit override
        const messageToSend = e?.injectedMessage || inputMessage;

        if (!messageToSend || !messageToSend.trim()) {
            toast.warning("Please enter your questions in the text area.");
            return;
        }

        if (isThinking) return; // prevent double submit

        setIsThinking(true);
        setIsBotTyping(true);
        setCurrentStreamingMessage(""); // clear previous stream
        setInputMessage(messageToSend.trim());
        
        const userMessage = {
            type: "user",
            name: user_nickname,
            text: messageToSend.trim(),
            timestamp: new Date().toISOString(),
        };

        const updatedMessages = [...chatMessages, userMessage];
        setChatMessages(updatedMessages);
        setInputMessage("");

        // reuse same streaming logic pattern
        let fullResponse = "";

        try {
            const apiInput = [
                { role: "system", content: systemInstruction },
                ...updatedMessages.map((msg) => ({
                    role: msg.type === "user" ? "user" : "assistant",
                    content: msg.text,
                })),
            ];

            fullResponse = await streamOpenAIResponse({
                model: "gpt-4o-mini",
                input: apiInput,
                temperature: 0.7,
                onTextDelta: (_delta, streamedText) => {
                    setCurrentStreamingMessage(streamedText);
                },
            });

            const aiMessage = {
                type: "ai",
                name: "ARI",
                text: fullResponse,
                timestamp: new Date().toISOString(),
            };

            const finalMessages = [...updatedMessages, aiMessage];
            await saveMessagesToStorage(finalMessages, messageToSend.trim());
            setChatMessages(finalMessages);
            setCurrentStreamingMessage("");
        } catch (err) {
            console.error(err);
        } finally {
            setIsThinking(false);
            setInputHeight('auto');
            setIsBotTyping(false);
        }
    };
    
    const saveMessagesToStorage = async (messages, firstQuestion='') => {
        var theThreadID = '';
        if(threadID){
            const { error } = await supabase
                .from("user_chat_history")
                .update({ messages })
                .eq("thread_id", threadID)
                .eq("user_id", userId);

            if (error) {
                console.error(error.message);
                return false;
            }
            theThreadID = threadID;
        }else{
            const { data, error } = await supabase
            .from("user_chat_history")
            .insert({
                user_id: userId,
                category_id: null,
                first_question: firstQuestion,
                messages: messages,
            }).select().maybeSingle();

            if (error) {
                toast.error("Problem creating conversation. " + error.message);
                return false;
            }
            
            theThreadID = data.thread_id;

            // clear textarea before redirect
            setThreadID(data.thread_id);
        }
        return theThreadID;
    };

    const clickSelectedQuestion = async (question) => {
        if (!question.trim()) {
            toast.error("Invalid question. Please refresh and try again.");
            return;
        }

        // optional: update textarea UI
        setInputMessage(question);

        // ✅ directly call onSubmit with injected value
        await onSubmit({
            preventDefault: () => {},
            injectedMessage: question, // custom property
        });
    };

    return (
        <>
            <section className={`chat-body-container ${userTheme}`}>
                {/* Sidebar */}
                <SideBar/> 
                
                {/* Main Chat Interface */}
                {(hasARIAccess) ? (
                    <div className="main-chat-container">
                        <div className="chat-box-top-container">
                            <a href="/" className='chat-heading-container'>
                                <span>Welcome to ARI</span>
                            </a>
                        </div>
                        
                        <div className="chat-box-center-container" ref={chatContainerRef}>

                            {(loadingMsg === true) ? 
                                (
                                    <div className="load-chat-spinner-loader">
                                        <img className='loaderCompanyLogo' src={companyLogo} alt="Loading..." />
                                        <img className='loaderSpinner' src={spinner} alt="Loading..." />
                                        <p>Fetching threads...</p>
                                    </div>
                                ) :
                                (
                                    (isThinking || threadID) ? (
                                        <>
                                            <div className="chat-ui-conversation-wrapper">
                                                <ul className="chat-item-list">
                                                    {/* Chat Messages */}
                                                    {chatMessages.map((message) => (
                                                        <li
                                                            className={`chat-item ${message.type === "user" ? "odd" : "even"}`}
                                                            key={message.timestamp}
                                                        >
                                                            <div className="chat-item-group">
                                                                <div className="chat-item-head">
                                                                    {message.type === "user" ? (
                                                                        <>
                                                                            <div className="chatuser-img"><RiUser3Line /></div>
                                                                            <h3 className="chatMsgName">{user_nickname}</h3>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <div
                                                                                className="chatbot-img"
                                                                                style={{ backgroundImage: `url(${companyLogo})` }}
                                                                            ></div>
                                                                            <h3>{message.name}</h3>
                                                                        </>
                                                                    )}
                                                                </div>

                                                                <div className="chat-item-body">
                                                                    <p id={message.appendID} dangerouslySetInnerHTML={{ __html:convertTextPattern(convertN2lbr(message.text))}} />
                                                                    <small className='data-time-txt'>{formatRelativeDate(message.timestamp)}</small>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    ))}

                                                    {/* Thinking indicator (only before stream starts) */}
                                                    {isThinking && !currentStreamingMessage && (
                                                        <li className="chat-item even">
                                                            <div className="chat-item-group">
                                                                <div className="chat-item-head">
                                                                    <div
                                                                        className="chatbot-img"
                                                                        style={{ backgroundImage: `url(${companyLogo})` }}
                                                                    ></div>
                                                                    <h3>ARI</h3>
                                                                </div>
                                                                <div className="chat-item-body">
                                                                    <div className="thinking-loader"><div className="thinking-dot"></div><div className="thinking-dot"></div><div className="thinking-dot"></div></div>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    )}

                                                    {/* Streaming bubble (ONLY while thinking) */}
                                                    {isThinking && currentStreamingMessage && (
                                                        <li className="chat-item even">
                                                            <div className="chat-item-group">
                                                                <div className="chat-item-head">
                                                                    <div
                                                                        className="chatbot-img"
                                                                        style={{ backgroundImage: `url(${companyLogo})` }}
                                                                    ></div>
                                                                    <h3>ARI</h3>
                                                                </div>
                                                                <div className="chat-item-body">
                                                                    <p dangerouslySetInnerHTML={{ __html:convertTextPattern(convertN2lbr(currentStreamingMessage))}} />
                                                                </div>
                                                            </div>
                                                        </li>
                                                    )}

                                                    <div ref={messagesEndRef} />
                                                </ul>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="chat-welcome-container">
                                            <h3 className="chat-icon-center">
                                                <BsChatDots />
                                            </h3>
                                            <h2>Welcome to ARI</h2>
                                            <div className="chat-box-suggestions-wrapper">
                                                <h5 className='text-center'>These topics are just starting points. Feel free to ask anything else that might be on your mind! 😊</h5>
                                                <ul>
                                                    {selectedQuestions.map((question, index) => (
                                                        <li key={index} className='text-red' onClick={() => clickSelectedQuestion(question.quest_desc)}>
                                                            <h6>{question.quest_icon} {question.category_id.category_name ?? 'Others'}: </h6>
                                                            <p>{question.quest_desc}</p>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    )
                                )
                            }
                        </div>
                        
                        <div className="chat-box-bottom-container">
                            <ChatBox
                                isBotTyping={isBotTyping}
                                onChange={onChange}
                                inputMessage={inputMessage}
                                inputHeight={inputHeight}
                                handleKeyDown={handleKeyDown}
                                formRef={formRef}
                                onSubmit={onSubmit}
                                categoryID={''}
                                textareaRef={textareaRef}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="main-chat-container">
                        <div className="chat-box-top-container">
                            <a href="/" className='chat-heading-container'>
                                <span>Welcome to ARI: Upgrade your plan.</span>
                            </a>
                        </div>
                        <div className="chat-box-center-container">
                            <UpgradeRequired />
                        </div>
                    </div>
                )}
                
            </section>
        </>
    );
};

export default Dashboard;